import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { Dialog, Disclosure, /* Menu, */ Transition } from "@headlessui/react";
import { navigate } from "@reach/router";
import { isEqual, isArray } from "lodash";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  //ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MinusSmallIcon,
  PlusSmallIcon,
  //Squares2X2Icon,
} from "@heroicons/react/24/solid";

import productOptionsConfig from "@/data/product-options.config";

import { getFancyPriceString } from "@/components/price-utils";
import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";

export const query = graphql`
  fragment ShopProductListWithInlineActionsAndExpandableSidebarFiltersProcessedProduct on ShopifyProcessedProduct {
    title
    url
    options {
      name
      values
    }
    featuredMedia {
      ...ShopifyMediaSubquery
    }
    priceRangeV2 {
      minVariantPrice {
        ...ShopifyMoneyV2Subquery
      }
      maxVariantPrice {
        ...ShopifyMoneyV2Subquery
      }
    }
    tags
  }

  fragment ShopProductListWithInlineActionsAndExpandableSidebarFiltersData on StrapiComponentShopProductListWithInlineActionsAndExpandableSidebarFilters {
    title
    topic
    filterText
    productTypeText
    closeMenuText
    filterRemoveButtonText
    noItemsFoundText
    noItemsFoundImage {
      ...StrapiMediaSubquery
    }
    paginationPrevious
    paginationNext
    paginationCurrentPageText
  }
`;

const itemsPerPage = 6;

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function WithInlineActionsAndExpandableSidebarFilters({
  data,
  shopData = null,
  products = [],
  location = null,
}) {
  if (!data) {
    return <></>;
  }

  const filterCategories = [
    ...(shopData?.availableOptions?.length
      ? shopData.availableOptions.map((option) => option.name)
      : []),
    ...(shopData?.availableTags?.length ? ["Tags"] : []),
  ];
  const filters = [
    ...(shopData?.availableOptions?.length
      ? shopData.availableOptions.map((option) => {
          return {
            name: option.name,
            values: [...option.values],
          };
        })
      : []),
    ...(shopData?.availableTags?.length
      ? [
          {
            name: "Tags",
            values: [...shopData.availableTags],
          },
        ]
      : []),
  ];

  const hasActiveFilter = (section, option, filter = activeFilters) => {
    return Boolean(
      filter.find(
        (filter) => filter.section === section && filter.option === option
      )
    );
  };

  const getSectionActiveFilters = (section) => {
    if (!activeFilters?.length) {
      return [];
    }

    return activeFilters.filter((filter) => filter.section === section);
  };

  const removeSectionActiveFilters = (section) => {
    const sectionActiveFilters = getSectionActiveFilters(section);
    if (!sectionActiveFilters?.length) {
      return false;
    }
    let newActiveFilters = [...activeFilters];
    for (let filter of sectionActiveFilters) {
      newActiveFilters = newActiveFilters.filter(
        (activeFilter) =>
          !isEqual(activeFilter, {
            section: filter.section,
            option: filter.option,
          })
      );
    }
    setActiveFilters(newActiveFilters);
    navigate(`?${activeFiltersToQueryString(newActiveFilters)}`, {
      replace: true,
    });
    setFilteredProducts(filterProducts(newActiveFilters));
    return true;
  };

  const addActiveFilter = (section, option) => {
    if (!hasActiveFilter(section, option)) {
      const newActiveFilters = [...activeFilters, { section, option }];
      setActiveFilters(newActiveFilters);
      navigate(`?${activeFiltersToQueryString(newActiveFilters)}`, {
        replace: true,
      });
      setFilteredProducts(filterProducts(newActiveFilters));
    }
  };

  const removeActiveFilter = (section, option) => {
    const newActiveFilters = [
      ...activeFilters.filter(
        (activeFilter) => !isEqual(activeFilter, { section, option })
      ),
    ];
    setActiveFilters(newActiveFilters);
    navigate(`?${activeFiltersToQueryString(newActiveFilters)}`, {
      replace: true,
    });
    setFilteredProducts(filterProducts(newActiveFilters));
  };

  const filterProducts = (activeFilters) => {
    if (!products?.length) {
      return [];
    }
    if (!activeFilters?.length) {
      return processProductCardOptions(products);
    }
    let activeProducts = [...products];
    for (let filterCategory of filterCategories) {
      let activeCategoryFilters = activeFilters.filter(
        (activeFilter) => activeFilter.section === filterCategory
      );
      if (activeCategoryFilters?.length) {
        if (filterCategory === "Tags") {
          activeProducts = activeProducts?.filter((product) =>
            Boolean(
              product.tags?.find((tag) =>
                hasActiveFilter("Tags", tag, activeCategoryFilters)
              )
            )
          );
        } else {
          activeProducts = activeProducts?.filter((product) =>
            Boolean(
              product.options?.find((option) =>
                option.values.find((value) =>
                  hasActiveFilter(option.name, value, activeCategoryFilters)
                )
              )
            )
          );
        }
      }
    }
    return processProductCardOptions(activeProducts);
  };

  function activeFiltersToQueryString(activeFilters) {
    let reducedFilters = activeFilters.reduce((combinedFilters, next) => {
      if (!combinedFilters[next.section]) {
        combinedFilters[next.section] = [];
      }
      combinedFilters[next.section].push(next.option);
      return combinedFilters;
    }, {});
    let str = [];
    for (var p in reducedFilters) {
      if (reducedFilters[p]) {
        str.push(
          encodeURIComponent(p) + "=" + arrayToQueryString(reducedFilters[p])
        );
      }
    }
    return str.join("&");
  }

  function arrayToQueryString(obj) {
    if (isArray(obj)) {
      var str = [];
      for (var p in obj) {
        if (obj[p]) {
          str.push(encodeURIComponent(obj[p]));
        }
      }
      return str.join(";");
    }
    return encodeURIComponent(obj);
  }

  function queryStringToActiveFilters(queryString) {
    let activeFilters = [];
    let sections = queryString?.substring(1)?.split("&") || [];
    for (let section of sections) {
      section = section.split("=");
      let sectionName = decodeURIComponent(section[0]);
      if (filterCategories.includes(sectionName)) {
        for (let option of section[1]?.split(";") || []) {
          option = decodeURIComponent(option);
          if (
            filters.find(
              (filter) =>
                filter.name === sectionName && filter.values.includes(option)
            )
          ) {
            activeFilters.push({
              section: sectionName,
              option,
            });
          }
        }
      }
    }
    return activeFilters;
  }

  const processProductCardOptions = (products) => {
    if (!products?.length) {
      return [];
    }
    return products.map((product) => {
      let colorOption =
        product.options?.find((option) =>
          productOptionsConfig?.colorOptions?.includes(option.name)
        ) || [];
      return { ...product, colorOption };
    });
  };

  const closeDialog = (setIsOpen, setDidJustClosed) => {
    setIsOpen(false);
    setDidJustClosed(true);
    setTimeout(() => setDidJustClosed(false), 10);
  };

  const [inactiveProductTypes] = useState(
    shopData?.allProductTypes?.filter(
      (productType) =>
        !shopData?.activeProductTypes.find(
          (activeProductType) => activeProductType.type === productType.type
        )
    ) || []
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [didJustClosedMobileFilters, setDidJustClosedMobileFilters] =
    useState(false);

  const [activeFilters, setActiveFilters] = useState(
    queryStringToActiveFilters(location?.search)
  );
  const [filteredProducts, setFilteredProducts] = useState(
    filterProducts(activeFilters)
  );

  const [currentPage, setCurrentPage] = useState(1);

  let totalPages = 0;

  function increasePage() {
    goToPage(currentPage + 1);
  }

  function decreasePage() {
    goToPage(currentPage - 1);
  }

  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (typeof document !== "undefined") {
        document.getElementById("divFirst").scrollIntoView();
      }
    }
  }

  function isPageActive(i) {
    return currentPage === i;
  }

  function isFirstPage() {
    return currentPage === 1;
  }

  function isLastPage() {
    return currentPage === totalPages;
  }

  let pageProducts = [...filteredProducts];

  totalPages = Math.ceil(pageProducts.length / itemsPerPage);

  let pageLinks = [];
  for (let i = 1; i <= totalPages; i++) {
    pageLinks.push(i);
  }
  let pageLinksLeft = null;
  let pageLinksMiddle = pageLinks;
  let pageLinksRight = null;
  if (totalPages >= 9) {
    if (currentPage > 4) pageLinksLeft = [1];

    let pageLinksMiddleStart =
      currentPage <= 4
        ? 0
        : currentPage >= totalPages - 3
        ? totalPages - 5
        : currentPage - 2;
    let pageLinksMiddleEnd =
      currentPage <= 4
        ? 5
        : currentPage >= totalPages - 3
        ? totalPages
        : currentPage + 1;
    pageLinksMiddle = pageLinks.slice(pageLinksMiddleStart, pageLinksMiddleEnd);

    if (currentPage < totalPages - 3) pageLinksRight = [totalPages];
  }
  pageProducts = pageProducts.slice(
    itemsPerPage * (currentPage - 1),
    itemsPerPage * (currentPage - 1) + itemsPerPage
  );

  return (
    <div id="divFirst" className="bg-white">
      {/* Mobile filter dialog */}
      <Transition.Root show={mobileFiltersOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={() =>
            closeDialog(setMobileFiltersOpen, setDidJustClosedMobileFilters)
          }
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40" />
          </Transition.Child>

          <div className="fixed inset-x-0 bottom-0 top-20 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="xs:min-w-[20rem] relative ml-auto flex h-full min-w-full max-w-lg flex-col bg-white shadow-xl">
                <div className="flex h-20 shrink-0 items-center justify-between border-b border-border-secondary-light bg-background-white px-4">
                  <h2 className="text-3xl font-bold text-text-primary">
                    {data.filterText || "Filters"}
                  </h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-text-primary-light hover:text-text-primary"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    {data.closeMenuText && (
                      <label className="sr-only">{data.closeMenuText}</label>
                    )}
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Filters */}
                <form className="flex h-full flex-col overflow-y-auto">
                  {Boolean(inactiveProductTypes?.length) && (
                    <div className="px-2 pb-6 pt-3">
                      <h3 className="px-2 py-3 text-xl font-semibold lg:text-lg">
                        {data.productTypeText || "Categories"}
                      </h3>
                      <ul
                        role="list"
                        className="px-2 text-lg font-medium text-text-primary lg:text-base"
                      >
                        {inactiveProductTypes.map((productType, index) => (
                          <li key={index}>
                            <LinkWrapper
                              to={productType.url}
                              className="px-2 py-3"
                            >
                              {productType.type}
                            </LinkWrapper>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Boolean(filters?.length) &&
                    filters.map(
                      (section, index) =>
                        Boolean(section?.values?.length) && (
                          <Disclosure
                            as="div"
                            key={index}
                            className="border-t border-border-secondary-light px-4 py-6"
                          >
                            {({ open }) => (
                              <>
                                <h3 className="-mx-2 -my-3 flow-root">
                                  <div className="flex w-full items-center bg-white text-lg text-text-secondary lg:text-base">
                                    <Disclosure.Button className="peer px-2 py-3">
                                      <span className="text-lg font-medium text-text-primary sm:text-base">
                                        {section.name}
                                      </span>
                                    </Disclosure.Button>
                                    {Boolean(
                                      getSectionActiveFilters(section.name)
                                        ?.length
                                    ) && (
                                      <div className="-my-2 mx-2 flex items-center rounded-full border border-border-secondary-light bg-white px-2 py-1 text-xs text-text-secondary">
                                        <span>{`${
                                          getSectionActiveFilters(section.name)
                                            ?.length
                                        } ${data.filterText}`}</span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            removeSectionActiveFilters(
                                              section.name
                                            );
                                          }}
                                          className="ml-1"
                                        >
                                          {data.filterRemoveButtonText && (
                                            <label className="sr-only">
                                              {data.filterRemoveButtonText}
                                            </label>
                                          )}
                                          <XMarkIcon className="h-4 w-4 hover:text-text-primary" />
                                        </button>
                                      </div>
                                    )}
                                    <Disclosure.Button className="flex grow justify-end px-2 py-3 hover:text-text-primary peer-hover:text-text-primary">
                                      <span className="ml-6 flex items-center">
                                        {open ? (
                                          <MinusSmallIcon
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        ) : (
                                          <PlusSmallIcon
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </span>
                                    </Disclosure.Button>
                                  </div>
                                </h3>
                                <Disclosure.Panel className="pt-6">
                                  <div className="space-y-6">
                                    {section.values.map(
                                      (option, optionIndex) => (
                                        <div
                                          key={optionIndex}
                                          className="flex items-center"
                                        >
                                          <input
                                            onChange={(event) => {
                                              if (event?.target?.checked) {
                                                addActiveFilter(
                                                  section.name,
                                                  option
                                                );
                                              } else {
                                                removeActiveFilter(
                                                  section.name,
                                                  option
                                                );
                                              }
                                            }}
                                            id={`filter-${index}-${optionIndex}`}
                                            type="checkbox"
                                            checked={hasActiveFilter(
                                              section.name,
                                              option
                                            )}
                                            className="h-4 w-4 rounded border-border-secondary-light text-text-primary focus:ring-ring-primary"
                                          />
                                          <label
                                            htmlFor={`filter-${index}-${optionIndex}`}
                                            className="ml-3 min-w-0 flex-1 text-text-secondary"
                                          >
                                            {option}
                                          </label>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        )
                    )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Main component */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="relative z-10 flex items-baseline justify-between border-b border-border-secondary-light pb-6">
          <div className="flex items-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-text-primary">
              {shopData.activeProductTypes?.[0]?.type ||
                data.title ||
                "Products"}
            </h1>
          </div>

          <div className="flex items-center">
            {/* Sort menu */}
            {/* <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="group inline-flex justify-center text-sm font-medium text-text-primary-light hover:text-text-primary">
                    Sort
                    <ChevronDownIcon
                      className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-text-secondary group-hover:text-text-secondary-dark"
                      aria-hidden="true"
                    />
                  </Menu.Button>
                </div>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {sortOptions.map((option) => (
                        <Menu.Item key={option.label}>
                          {({ active }) => (
                            <a
                              href={option.href}
                              className={classNames(
                                option.current
                                  ? "font-medium text-text-primary"
                                  : "text-text-secondary-dark",
                                active ? "bg-background-secondary-light" : "",
                                "block px-4 py-2 text-sm"
                              )}
                            >
                              {option.label}
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu> */}

            <button
              type="button"
              className="-m-2 ml-4 p-2 text-text-secondary hover:text-text-secondary-dark sm:ml-6 lg:hidden"
              onClick={() =>
                !didJustClosedMobileFilters &&
                setMobileFiltersOpen(!mobileFiltersOpen)
              }
            >
              <span className="sr-only">{data.filterText || "Filters"}</span>
              <FunnelIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <section aria-labelledby="products-heading" className="pb-24 pt-6">
          <h2 id="products-heading" className="sr-only">
            {shopData.activeProductTypes?.[0]?.type || data.title || "Products"}
          </h2>

          <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
            {/* Filters */}
            <form className="hidden lg:block">
              {Boolean(inactiveProductTypes?.length) && (
                <div>
                  <h3 className="text-base font-semibold">
                    {data.productTypeText || "Categories"}
                  </h3>
                  <ul
                    role="list"
                    className="space-y-4 border-b border-border-secondary-light px-2 py-3 pb-6 text-sm font-medium text-text-primary"
                  >
                    {inactiveProductTypes.map((productType, index) => (
                      <li key={index}>
                        <LinkWrapper
                          to={productType.url}
                          className="hover:text-text-primary-light"
                        >
                          {productType.type}
                        </LinkWrapper>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Boolean(filters?.length) &&
                filters.map(
                  (section, index) =>
                    Boolean(section?.values?.length) && (
                      <Disclosure
                        as="div"
                        key={index}
                        className="border-b border-border-secondary-light py-6"
                      >
                        {({ open }) => (
                          <>
                            <h3 className="-my-3 flow-root">
                              <div className="flex w-full items-center bg-white">
                                <Disclosure.Button className="peer py-3 text-sm text-text-primary">
                                  <span className="font-medium">
                                    {section.name}
                                  </span>
                                </Disclosure.Button>
                                {Boolean(
                                  getSectionActiveFilters(section.name)?.length
                                ) && (
                                  <div className="-my-2 mx-2 flex items-center rounded-full border border-border-secondary-light bg-white px-2 py-1 text-xs text-text-secondary">
                                    <span>{`${
                                      getSectionActiveFilters(section.name)
                                        ?.length
                                    } ${data.filterText}`}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        removeSectionActiveFilters(
                                          section.name
                                        );
                                      }}
                                      className="ml-1"
                                    >
                                      {data.filterRemoveButtonText && (
                                        <label className="sr-only">
                                          {data.filterRemoveButtonText}
                                        </label>
                                      )}
                                      <XMarkIcon className="h-4 w-4 hover:text-text-primary" />
                                    </button>
                                  </div>
                                )}
                                <Disclosure.Button className="flex grow justify-end py-3 text-sm text-text-secondary-light hover:text-text-primary peer-hover:text-text-primary">
                                  <span className="ml-6 flex items-center">
                                    {open ? (
                                      <MinusSmallIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <PlusSmallIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </Disclosure.Button>
                              </div>
                            </h3>
                            <Disclosure.Panel className="pt-6">
                              <div className="space-y-4">
                                {section.values.map((option, optionIndex) => (
                                  <div
                                    key={optionIndex}
                                    className="flex items-center"
                                  >
                                    <input
                                      onChange={(event) => {
                                        if (event?.target?.checked) {
                                          addActiveFilter(section.name, option);
                                        } else {
                                          removeActiveFilter(
                                            section.name,
                                            option
                                          );
                                        }
                                      }}
                                      id={`filter-${index}-${optionIndex}`}
                                      type="checkbox"
                                      checked={hasActiveFilter(
                                        section.name,
                                        option
                                      )}
                                      className="h-4 w-4 rounded border-border-secondary-light text-text-primary focus:ring-ring-primary"
                                    />
                                    <label
                                      htmlFor={`filter-${index}-${optionIndex}`}
                                      className="ml-3 min-w-0 flex-1 text-text-secondary"
                                    >
                                      {option}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    )
                )}
            </form>

            {/* Product grid */}
            <div className="col-span-3">
              <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-8 xl:grid-cols-3">
                {pageProducts?.length ? (
                  pageProducts.map((product, index) => (
                    <div
                      key={index}
                      className="group relative flex flex-col overflow-hidden rounded-sm border border-border-secondary-light bg-white"
                    >
                      <LinkWrapper to={product.url}>
                        <div className="aspect-[1/1] bg-background-secondary-light group-hover:opacity-75">
                          {product.featuredMedia && (
                            <MediaWrapper
                              media={product.featuredMedia}
                              objectFit="cover"
                              divClassName="aspect-[1/1] h-full"
                            />
                          )}
                        </div>
                        <div className="flex flex-1 flex-col space-y-2 p-4">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {product.title}
                          </h3>
                          <div className="flex flex-1 flex-col justify-end">
                            <p className="text-lg font-medium text-text-primary">
                              {product.priceRangeV2?.minVariantPrice?.amount !==
                                product.priceRangeV2?.maxVariantPrice?.amount &&
                                data.minVariantPriceText && (
                                  <span className="pr-1 text-sm">
                                    {data.minVariantPriceText}
                                  </span>
                                )}
                              {getFancyPriceString(
                                product.priceRangeV2?.minVariantPrice,
                                "de"
                              )}
                            </p>
                          </div>
                          {Boolean(product?.colorOption?.values?.length) && (
                            <div className="h-10" />
                          )}
                        </div>
                      </LinkWrapper>
                      {Boolean(product?.colorOption?.values?.length) && (
                        <div className="absolute bottom-4 left-4 flex items-center">
                          {[...(product.colorOption.values || [])]
                            .filter(
                              (color) => productOptionsConfig?.colorMap[color]
                            )
                            .slice(0, 4)
                            .map((color, colorIndex) => (
                              <LinkWrapper
                                key={colorIndex}
                                to={`${product?.url}?${product?.colorOption?.name}=${color}`}
                                className={classNames(
                                  "relative mr-2 flex aspect-[1/1] w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border-light-gray ring-offset-2 hover:ring hover:ring-ring-primary-light focus:outline-none"
                                )}
                              >
                                <label className="sr-only">{color}</label>
                                <svg
                                  className={classNames(
                                    "absolute inset-0 h-full w-full"
                                  )}
                                  viewBox="0 0 100 100"
                                  preserveAspectRatio="none"
                                >
                                  <rect
                                    fill={
                                      Array.isArray(
                                        productOptionsConfig?.colorMap[color]
                                      )
                                        ? productOptionsConfig?.colorMap[
                                            color
                                          ][0]
                                        : productOptionsConfig?.colorMap[color]
                                    }
                                    width="100"
                                    height="100"
                                    x="0"
                                    y="0"
                                  />
                                  <rect
                                    fill={
                                      Array.isArray(
                                        productOptionsConfig?.colorMap[color]
                                      )
                                        ? productOptionsConfig?.colorMap[
                                            color
                                          ][1]
                                        : productOptionsConfig?.colorMap[color]
                                    }
                                    width="60"
                                    height="120"
                                    x="-8"
                                    y="-65"
                                    transform="rotate(45)"
                                  />
                                  <rect
                                    fill={
                                      Array.isArray(
                                        productOptionsConfig?.colorMap[color]
                                      )
                                        ? productOptionsConfig?.colorMap[
                                            color
                                          ][2]
                                        : productOptionsConfig?.colorMap[color]
                                    }
                                    width="60"
                                    height="120"
                                    x="88"
                                    y="-65"
                                    transform="rotate(45)"
                                  />
                                </svg>
                              </LinkWrapper>
                            ))}
                          {Boolean(
                            product?.colorOption?.values?.length > 4
                          ) && (
                            <LinkWrapper
                              to={product?.url}
                              className={classNames(
                                "relative mr-2 flex aspect-[1/1] w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border-light-gray ring-offset-2 hover:ring hover:ring-ring-primary-light focus:outline-none"
                              )}
                            >
                              <label className="sr-only">{"Mehr"}</label>
                              <PlusSmallIcon
                                className={classNames(
                                  "absolute inset-0 h-full w-full text-text-secondary"
                                )}
                              />
                            </LinkWrapper>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="mx-auto mt-12 flex max-w-lg justify-center lg:max-w-none">
                    {data.noItemsFoundImage ? (
                      <>
                        <span className="sr-only">
                          {data.noItemsFoundText || "No Posts Found"}
                        </span>
                        <MediaWrapper media={data.noItemsFoundImage} />
                      </>
                    ) : (
                      <span className="text-center text-lg font-bold">
                        {data.noItemsFoundText || "No Posts Found"}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between bg-white px-4">
                  <div className="flex flex-1 items-center justify-between sm:hidden">
                    <button
                      onClick={decreasePage}
                      className={`inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                        isFirstPage(currentPage)
                          ? "cursor-default opacity-0"
                          : ""
                      }`}
                      disabled={isFirstPage(currentPage)}
                    >
                      {data.paginationPrevious
                        ? data.paginationPrevious
                        : "Previous"}
                    </button>
                    <span className="px-4 py-2 text-sm font-medium text-text-gray">{`${
                      data.paginationCurrentPageText
                        ? data.paginationCurrentPageText
                        : "Page"
                    } ${currentPage}/${totalPages}`}</span>
                    <button
                      onClick={increasePage}
                      className={`ml-3 inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                        isLastPage(currentPage)
                          ? "cursor-default opacity-0"
                          : ""
                      }`}
                      disabled={isLastPage(currentPage)}
                    >
                      {data.paginationNext ? data.paginationNext : "Next"}
                    </button>
                  </div>
                  <div className="hidden items-center justify-center sm:flex sm:flex-1">
                    <div
                      className="inline-flex bg-background-white text-center text-sm font-medium text-text-primary shadow-sm"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={decreasePage}
                        className={`inline-flex items-center justify-center rounded-l-md border border-border-light-gray px-2 py-2 ${
                          isFirstPage(currentPage)
                            ? "cursor-default text-text-light-gray"
                            : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                        }`}
                        disabled={isFirstPage(currentPage)}
                      >
                        <span className="sr-only">
                          {data.paginationPrevious
                            ? data.paginationPrevious
                            : "Previous"}
                        </span>
                        <ChevronLeftIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                      {Boolean(pageLinksLeft?.length) && (
                        <>
                          {pageLinksLeft.map((pageLink, index) => (
                            <button
                              key={index}
                              onClick={() => goToPage(pageLink)}
                              aria-current="page"
                              className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                            >
                              {pageLink}
                            </button>
                          ))}
                          <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                            ...
                          </span>
                        </>
                      )}
                      {Boolean(pageLinksMiddle?.length) &&
                        pageLinksMiddle.map((pageLink, index) => (
                          <button
                            key={index}
                            onClick={() => goToPage(pageLink)}
                            aria-current="page"
                            className={`inline-flex w-10 items-center justify-center px-4 py-2 ${
                              isPageActive(pageLink)
                                ? "z-10 cursor-default bg-background-primary text-text-on-primary-light"
                                : "border border-border-light-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                            }`}
                            disabled={isPageActive(pageLink)}
                          >
                            {pageLink}
                          </button>
                        ))}
                      {Boolean(pageLinksRight?.length) && (
                        <>
                          <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                            ...
                          </span>
                          {pageLinksRight.map((pageLink, index) => (
                            <button
                              key={index}
                              onClick={() => goToPage(pageLink)}
                              aria-current="page"
                              className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                            >
                              {pageLink}
                            </button>
                          ))}
                        </>
                      )}
                      <button
                        onClick={increasePage}
                        className={`inline-flex items-center justify-center rounded-r-md border border-border-light-gray px-2 py-2 ${
                          isLastPage(currentPage)
                            ? "cursor-default text-text-light-gray"
                            : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                        }`}
                        disabled={isLastPage(currentPage)}
                      >
                        <span className="sr-only">
                          {data.paginationNext ? data.paginationNext : "Next"}
                        </span>
                        <ChevronRightIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

WithInlineActionsAndExpandableSidebarFilters.propTypes = {
  data: PropTypes.object,
  shopData: PropTypes.object,
};

export default WithInlineActionsAndExpandableSidebarFilters;
